import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import API from "../../config/axios";
import { api_paths } from "../../config/api.paths";
import SelectField from "../common/SelectField";
import { toastSuccessMessage, toastErrorMessage } from "../../utils/utils";
import { editMobileSchema } from "../../validators/validatorSchema";
import { validateData, clearError } from "../../validators/YupValidations";

// -------------------------------------------
// CONSTANTS
// -------------------------------------------
const COUNTRY_CODE_OPTIONS = [
  { value: "+1", label: "+1" },
  { value: "+91", label: "+91" },
];

const INITIAL_FORM = {
  phone: "",
  country_code: "+1",
};

// -------------------------------------------
// COMPONENT
// -------------------------------------------
const EditMobileModal = ({
  show,
  onHide,
  editData,
  refreshList,
  onOtpOpen,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // -------------------------------------------
  // LOAD EDIT DATA INTO FORM
  // -------------------------------------------
  useEffect(() => {
    if (!show) return;

    setForm({
      phone: editData?.phone || "",
      country_code: editData?.country_code || "+1",
    });
    setErrors({});
  }, [show, editData]);

  // -------------------------------------------
  // UPDATE FIELD
  // -------------------------------------------
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) clearError(setErrors, field);
  };

  // -------------------------------------------
  // SUBMIT HANDLER
  // -------------------------------------------
  // inside EditMobileModal component
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateData(editMobileSchema, form, setErrors);
    if (!isValid) return;

    try {
      setLoading(true);

      const response = await API.post(api_paths.member.EDIT_PHONE, {
        phone: form.phone,
        country_code: form.country_code,
      });

      // adjust this path depending on your API structure:
      const otpId = response?.data?.payload?.result || null;

      if (!otpId) {
        // if API doesn't return otp id but returns success, still open OTP modal without id
        toastSuccessMessage("OTP sent successfully");
        refreshList();
        onHide();
        onOtpOpen && onOtpOpen(null);
        return;
      }

      // success with otp id
      toastSuccessMessage("OTP sent successfully");
      refreshList();
      onHide();

      // PASS otpId to parent so it can pass it to OtpModal
      onOtpOpen && onOtpOpen(otpId);
    } catch (err) {
      toastErrorMessage("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------
  // JSX
  // -------------------------------------------
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center fw-semibold update-mo-title">
          Update Mobile Number
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body className="pt-3 px-4 pb-4">
          <label className="small fw-semibold mb-2">Mobile Number</label>

          <div className="d-flex gap-2">
            {/* Country Code */}
            <div style={{ width: "100px" }}>
              <SelectField
                value={COUNTRY_CODE_OPTIONS.find(
                  (opt) => opt.value === form.country_code
                )}
                options={COUNTRY_CODE_OPTIONS}
                onChange={(opt) => handleChange("country_code", opt.value)}
              />
            </div>

            {/* Phone */}
            <div className="flex-grow-1">
              <input
                type="tel"
                placeholder="Enter Mobile Number"
                className={`form-control form-control-sm ${
                  errors.phone ? "is-invalid" : ""
                }`}
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                maxLength={10}
              />

              {errors.phone && (
                <div className="invalid-feedback d-block">
                  {errors.phone.message}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 px-4 pb-4">
          <Button
            type="submit"
            className="w-100 btn-orange-lg"
            style={{
              backgroundColor: "#F4A528",
              border: "none",
              fontWeight: 600,
              fontSize: "16px",
              borderRadius: "8px",
            }}
            disabled={loading}
          >
            {loading ? "Sending OTP..." : "Send OTP"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditMobileModal;
